import { mathCalculationService } from "../../../../../../shared/services/MathCalculationService";
import type { MarketDataDto } from "../../../../../../shared/types/MarketDataDto";
import { PortfolioRecord } from "../../../../../../shared/types/PortfolioRecord";
import { PortfolioProfile } from "../../../../../../shared/types/PortfolioProfile";
import { ChartSeries } from "../types/ChartSeries";
import { PortfolioDates } from "../../../../../../shared/hooks/use-portfolio-service/PortfolioDates";
import { dateTimeConversionService } from "../../../../../../shared/services/DateTimeConversionService";
import { chartTooltipService } from "../../portfolio-greek-profile/services/ChartTooltipService";
import {
    PortfolioPerformanceContext,
    portfolioPerformanceService
} from "../../../../../../shared/services/PortfolioPerformanceService";

class BalanceProfileTooltipService {
    readonly currentBalanceName = 'Current balance';
    readonly comparedCurrentBalanceName = 'Compared current balance';
    readonly customBalance1Name = 'Custom balance 1';
    readonly customBalance2Name = 'Custom balance 2';
    readonly customBalance3Name = 'Custom balance 3';
    readonly comparedCustomBalanceName = 'Compared custom balance';
    readonly expirationBalanceName = 'Expiration balance';
    readonly comparedExpirationBalanceName = 'Compared expiration balance';
    
    getTooltipContent(
        seriesCollection: ChartSeries[],
        marketDataDto: MarketDataDto,
        portfolioProfile: PortfolioProfile,
        comparedPortfolioProfile: PortfolioProfile,
        selectedPortfolioDates: PortfolioDates,
        showPortfolioPerformance: boolean,
        usePortfolioAnnualisedRoi: boolean,
        selectedPerformanceContext: PortfolioPerformanceContext,
        comparedPerformanceContext: PortfolioPerformanceContext,
        balanceDisplayShift: number,
    ): string {
        if (seriesCollection.length === 0)
            return "No one series isn't selected";
        
        const hasAnyComparedBalanceProfile = this.containAnyComparedBalanceProfile(seriesCollection);
        const tooltipContent = this.getTooltipData(
            seriesCollection,
            marketDataDto,
            hasAnyComparedBalanceProfile,
            portfolioProfile,
            comparedPortfolioProfile,
            selectedPortfolioDates,
            showPortfolioPerformance,
            usePortfolioAnnualisedRoi,
            selectedPerformanceContext,
            comparedPerformanceContext,
            balanceDisplayShift,
        );
        
        if (tooltipContent === null)
            return "Error: Current balance isn't available";

        const tooltipHeader = this.getTooltipHeader(
            seriesCollection,
            hasAnyComparedBalanceProfile,
            marketDataDto,
            showPortfolioPerformance,
        );
        const tooltipDivider = chartTooltipService.getTooltipDivider();
        
        return tooltipHeader + tooltipDivider + tooltipContent;
    }

    private getCurrentPortfolioRecord(
        marketDataDto: MarketDataDto,
        portfolioRecords: PortfolioRecord[]
    ): PortfolioRecord | undefined {
        if (marketDataDto === undefined || marketDataDto.UnderlyingPrice === null)
            return;

        const underlyingPrice = marketDataDto.UnderlyingPrice;

        for (let x = 0; x < portfolioRecords.length - 1; x++) {
            if (underlyingPrice >= portfolioRecords[x].price && underlyingPrice < portfolioRecords[x + 1].price) {
                const middlePrice = (portfolioRecords[x].price + portfolioRecords[x + 1].price) / 2;

                return underlyingPrice <= middlePrice
                    ? portfolioRecords[x]
                    : portfolioRecords[x + 1];
            }
        }
    }

    private getTooltipHeader(
        seriesCollection: ChartSeries[],
        hasAnyComparedBalanceProfile: boolean,
        marketDataDto: MarketDataDto,
        showPortfolioPerformance: boolean,
    ): string {
        const hoveredCurrentPrice = seriesCollection[0].axisValue;
        const valueWidth = 70;
        const diffWidth = 50;

        const deltaPrice = hoveredCurrentPrice - marketDataDto!.UnderlyingPrice!;
        const deltaPriceToDisplay = deltaPrice > 0
            ? `+${deltaPrice.toFixed(0)}`
            : deltaPrice.toFixed(0);

        return `
            <div>
                <div style="display: inline-block; width: ${hasAnyComparedBalanceProfile ? 210 : 195}px;"><b>Price: ${hoveredCurrentPrice}, ${deltaPriceToDisplay}</b></div>
                <div style="display: inline-block; text-align: right; width: ${valueWidth}px; font-size: 12px;"><b>Balance</b></div>
                <div style="display: inline-block; text-align: right; width: ${diffWidth}px; font-size: 12px;"><b>Diff</b></div>
                <div style="display: inline-block; text-align: right; width: ${valueWidth}px; font-size: 12px;"><b>Δ Balance</b></div>
                ${hasAnyComparedBalanceProfile
                    ? `<div style="display: inline-block; text-align: right; width: ${diffWidth}px; font-size: 12px;"><b>Diff</b></div>`
                    : ''}
            </div>`;
    }
    
    private getTooltipData(
        seriesCollection: ChartSeries[],
        marketDataDto: MarketDataDto,
        hasAnyComparedBalanceProfile: boolean,
        portfolioProfile: PortfolioProfile,
        comparedPortfolioProfile: PortfolioProfile,
        selectedPortfolioDates: PortfolioDates,
        showPortfolioPerformance: boolean,
        usePortfolioAnnualisedRoi: boolean,
        selectedPerformanceContext: PortfolioPerformanceContext,
        comparedPerformanceContext: PortfolioPerformanceContext,
        balanceDisplayShift: number,
    ): string | null {
        const currentPortfolioRecord = this.getCurrentPortfolioRecord(marketDataDto, portfolioProfile.data);
        const comparedPortfolioRecord = hasAnyComparedBalanceProfile
            ? this.getCurrentPortfolioRecord(marketDataDto, comparedPortfolioProfile.data)
            : null;

        if (!currentPortfolioRecord)
            return null;
        
        let tooltipToDisplay = '';
        
        for (let x = 0; x < seriesCollection.length; x++) {
            const seriesName = seriesCollection[x].seriesName;

            let metricName: string = '';
            let targetDate: string = '';
            let shift: string = '';
            
            let balanceAtUnderlyingPrice = 0;
            let balanceAtUnderlyingPriceForCurrent: number | null = null;
            let valueIndex = 0;
            let currentValueIndex: number | null = null;
            let customValueColour: string | null = null;

            const tooltipDateFormat = "DD.MMM HH:mm";
            const roundPrecision = 0;
            const customDateValueColour = "#FFB8B8FF";

            const delta1 = selectedPortfolioDates.custom1 !== null
                ? mathCalculationService.round((selectedPortfolioDates.custom1.getTime() - selectedPortfolioDates.current.getTime()) / 1000 / 60 / 60, roundPrecision)
                : 0;
            const delta2 = selectedPortfolioDates.custom2 !== null && selectedPortfolioDates.custom1 !== null
                ? mathCalculationService.round((selectedPortfolioDates.custom2.getTime() - selectedPortfolioDates.custom1.getTime()) / 1000 / 60 / 60, roundPrecision)
                : 0;
            const delta3 = selectedPortfolioDates.custom3 !== null && selectedPortfolioDates.custom2 !== null
                ? mathCalculationService.round((selectedPortfolioDates.custom3.getTime() - selectedPortfolioDates.custom2.getTime()) / 1000 / 60 / 60, roundPrecision)
                : 0;
            const delta4 = selectedPortfolioDates.nearestExpiration !== null && selectedPortfolioDates.custom3 !== null
                ? mathCalculationService.round((selectedPortfolioDates.nearestExpiration.getTime() - selectedPortfolioDates.custom3.getTime()) / 1000 / 60 / 60, roundPrecision)
                : 0;

            switch (seriesName) {
                case this.currentBalanceName:
                    metricName = "Current";
                    targetDate = dateTimeConversionService.convertDateToCustomFormattedString(selectedPortfolioDates.current, tooltipDateFormat);
                    shift = "+0H";
                    balanceAtUnderlyingPrice = currentPortfolioRecord.currentDateBalance;
                    valueIndex = 1;
                    break;
                case this.customBalance1Name:
                    metricName = "Custom 1";
                    targetDate = dateTimeConversionService.convertDateToCustomFormattedString(selectedPortfolioDates.custom1, tooltipDateFormat);
                    shift = `+${delta1}H`;
                    customValueColour = customDateValueColour;
                    balanceAtUnderlyingPrice = currentPortfolioRecord.customDateBalance1;
                    valueIndex = 2;
                    currentValueIndex = 1;
                    break;
                case this.customBalance2Name:
                    metricName = "Custom 2";
                    targetDate = dateTimeConversionService.convertDateToCustomFormattedString(selectedPortfolioDates.custom2, tooltipDateFormat);
                    shift = `+${delta2}H`;
                    customValueColour = customDateValueColour;
                    balanceAtUnderlyingPrice = currentPortfolioRecord.customDateBalance2;
                    valueIndex = 3;
                    currentValueIndex = 1;
                    break;
                case this.customBalance3Name:
                    metricName = "Custom 3";
                    targetDate = dateTimeConversionService.convertDateToCustomFormattedString(selectedPortfolioDates.custom3, tooltipDateFormat);
                    shift = `+${delta3}H`;
                    customValueColour = customDateValueColour;
                    balanceAtUnderlyingPrice = currentPortfolioRecord.customDateBalance3;
                    valueIndex = 4;
                    currentValueIndex = 1;
                    break;
                case this.expirationBalanceName:
                    metricName = "Expiration";
                    targetDate = dateTimeConversionService.convertDateToCustomFormattedString(selectedPortfolioDates.nearestExpiration, tooltipDateFormat);
                    shift = `+${delta4}H`;
                    balanceAtUnderlyingPrice = currentPortfolioRecord.expirationDateBalance;
                    valueIndex = 5;
                    currentValueIndex = 1;
                    break;

                case this.comparedCurrentBalanceName:
                    if (comparedPortfolioRecord) {
                        metricName = "⇄ Current"
                        balanceAtUnderlyingPrice = comparedPortfolioRecord.currentDateBalance;
                        balanceAtUnderlyingPriceForCurrent = currentPortfolioRecord.currentDateBalance;
                        valueIndex = 6;
                        currentValueIndex = 1;
                    }
                    break;
                case this.comparedCustomBalanceName:
                    if (comparedPortfolioRecord) {
                        metricName = "⇄ Custom"
                        balanceAtUnderlyingPrice = comparedPortfolioRecord.customDateBalance1;
                        balanceAtUnderlyingPriceForCurrent = currentPortfolioRecord.customDateBalance1;
                        valueIndex = 7;
                        currentValueIndex = 2;
                    }
                    break;
                case this.comparedExpirationBalanceName:
                    if (comparedPortfolioRecord) {
                        metricName = "⇄ Expiration"
                        balanceAtUnderlyingPrice = comparedPortfolioRecord.expirationDateBalance;
                        balanceAtUnderlyingPriceForCurrent = currentPortfolioRecord.expirationDateBalance;
                        valueIndex = 8;
                        currentValueIndex = 3;
                    }
                    break;
            }
            
            const isComparedBalance =
                seriesName === this.comparedCurrentBalanceName ||
                seriesName === this.comparedCustomBalanceName ||
                seriesName === this.comparedExpirationBalanceName;
            const performanceContext = isComparedBalance
                ? comparedPerformanceContext
                : selectedPerformanceContext;
            const formatBalance = (value: number, alwaysUseSign: boolean = false): string =>
                showPortfolioPerformance
                    ? portfolioPerformanceService.formatMetric(
                        value,
                        performanceContext,
                        usePortfolioAnnualisedRoi,
                        alwaysUseSign,
                    )
                    : alwaysUseSign
                        ? this.getFormattedNumberWithSign(value)
                        : value.toFixed(2);

            const balance = seriesCollection[x].value[valueIndex] - balanceDisplayShift;
            const balanceToDisplay = formatBalance(balance);

            const deltaBalance = (balance - (balanceAtUnderlyingPrice - balanceDisplayShift));
            const deltaBalanceToDisplay = formatBalance(deltaBalance, true);

            const comparedAndBalancePortfoliosDifference = currentValueIndex !== null
                ? balance - (seriesCollection[x].value[currentValueIndex] - balanceDisplayShift)
                : null;
            
            const comparedAndBalanceDifferenceToDisplay = comparedAndBalancePortfoliosDifference !== null
                ? formatBalance(comparedAndBalancePortfoliosDifference, true)
                : '';
            
            const currentAndExpirationBalancesDeltaColour = '#ffe07a';
            
            const comparedAndBalanceDifferenceColour = comparedAndBalancePortfoliosDifference !== null
                ? comparedAndBalancePortfoliosDifference < 0
                    ? '#f8a8a8'
                    : comparedAndBalancePortfoliosDifference > 0
                        ? '#b8faa8'
                        : null
                : null;

            const deltaBalancesDifference = currentValueIndex !== null && balanceAtUnderlyingPriceForCurrent !== null
                ? deltaBalance - (
                    (seriesCollection[x].value[currentValueIndex] - balanceDisplayShift) -
                    (balanceAtUnderlyingPriceForCurrent - balanceDisplayShift)
                )
                : null;
            
            const deltaBalancesDifferenceToDisplay = deltaBalancesDifference !== null
                ? formatBalance(deltaBalancesDifference, true)
                : '';
            
            const deltaBalancesDifferenceColour = deltaBalancesDifference !== null
                ? deltaBalancesDifference < 0
                    ? '#f8a8a8'
                    : deltaBalancesDifference > 0
                        ? '#b8faa8'
                        : null
                : null;

            const isCustomBalance1Series = seriesName === this.customBalance1Name;
            const isCustomBalance2Series = seriesName === this.customBalance2Name;
            const isCustomBalance3Series = seriesName === this.customBalance3Name;
            const isExpirationBalanceSeries = seriesName === this.expirationBalanceName;
            
            const currentAndExpirationBalancesDeltaToDisplay = isExpirationBalanceSeries && currentValueIndex !== null
                ? formatBalance(balance - (seriesCollection[x].value[currentValueIndex] - balanceDisplayShift), true)
                : isCustomBalance1Series && currentValueIndex !== null
                    ? formatBalance(balance - (seriesCollection[x].value[currentValueIndex] - balanceDisplayShift), true)
                    : isCustomBalance2Series && currentValueIndex !== null
                        ? formatBalance(balance - (seriesCollection[x].value[currentValueIndex] - balanceDisplayShift), true)
                        : isCustomBalance3Series && currentValueIndex !== null
                            ? formatBalance(balance - (seriesCollection[x].value[currentValueIndex] - balanceDisplayShift), true)
                            : null
            
            // border-right-width: 1px; border-right-color: #FFFFFF; border-right-style: dashed; padding-right: 5px

            const containerWidth = hasAnyComparedBalanceProfile ? 210 : 195;
            const metricCellStyle = "display:inline-block;font-size:12px";
            const valueWidth = 70;
            const diffWidth = 50;

            const isPrimaryBalance =
                isExpirationBalanceSeries ||
                isCustomBalance3Series ||
                isCustomBalance2Series ||
                isCustomBalance1Series;

            const value = isPrimaryBalance
                ? currentAndExpirationBalancesDeltaToDisplay
                : comparedAndBalanceDifferenceToDisplay;

            const colour = isPrimaryBalance
                ? currentAndExpirationBalancesDeltaColour
                : comparedAndBalanceDifferenceColour;

            const diffCellStyle = `display:inline-block;text-align:right;width:${diffWidth}px;font-size:11px`;
            
            tooltipToDisplay += `
                <div>
                    <div style="display:inline-block;width:${containerWidth}px;">
                      <div style="display:inline-block;width:70px;color:${customValueColour};">${metricName}</div>
                      <div style="${metricCellStyle};width:40px;text-align:right;color:${customValueColour};">${shift}</div>
                      <div style="${metricCellStyle};width:100px;text-align:center;color:${customValueColour};">${targetDate}</div>
                    </div>
                    <div style="display: inline-block; text-align: right; width: ${valueWidth}px">${balanceToDisplay}</div>
                    <div style="${diffCellStyle};color:${colour};">${value}</div>
                    <div style="display: inline-block; width: ${valueWidth}px; text-align: right;">${deltaBalanceToDisplay}</div>
                    ${hasAnyComparedBalanceProfile
                        ? `<div style="display: inline-block; width: ${diffWidth}px; text-align: right; font-size: 11px; color: ${deltaBalancesDifferenceColour}">${deltaBalancesDifferenceToDisplay}</div>`
                        : ''
                    }
                </div>`;
        }
        
        return tooltipToDisplay;
    }
    
    private containAnyComparedBalanceProfile(seriesCollection: ChartSeries[]): boolean {
        for (let x = 0; x < seriesCollection.length; x++) {
            const seriesName = seriesCollection[x].seriesName;

            switch (seriesName) {
                case this.comparedCurrentBalanceName:
                case this.comparedCustomBalanceName:
                case this.comparedExpirationBalanceName:
                    return true;
            }
        }

        return false;
    }

    private getFormattedNumberWithSign(value: number): string {
        return mathCalculationService.round(value, 2) > 0
            ? `+${value.toFixed(2)}`
            : value.toFixed(2)
    }
}

export const balanceProfileTooltipService = new BalanceProfileTooltipService();
